import Typography from '@mui/material/Typography'
import { FC, useRef, useState } from 'react'
import GridContainer from '../GridContainer'
import Testimonial from '../Testimonial'
import { Rating } from './components'

// Ratings data is hard-coded for now
const ratings = [
  {
    id: 'appStore',
    altText: 'Apple App Store',
    starRating: '4.9',
    numberOfReviews: '468K+',
    logoImageSrc: 'app-store-logo.svg',
    href: 'https://apps.apple.com/us/app/mission-lane-card/id1228440036',
    ctaName: 'Download App - App Store',
  },
  {
    id: 'playStore',
    altText: 'Google Play Store',
    starRating: '4.8',
    numberOfReviews: '180K+',
    logoImageSrc: 'google-play-logo.svg',
    href: 'https://play.google.com/store/apps/details?id=com.lcard_app&hl=en_US&gl=US&pli=1',
    ctaName: 'Download App - Google Play',
  },
  {
    id: 'trustpilot',
    altText: 'Trustpilot',
    starRating: '4.5',
    numberOfReviews: '8700+',
    logoImageSrc: 'trustpilot-logo.png',
    href: 'https://www.trustpilot.com/review/missionlane.com',
  },
]

const testimonials = [
  {
    comment: 'I wish all my credit cards were this easy to manage.',
    customerName: 'Jason F.',
    customerLocation: 'Bonner Springs, KS',
  },
  {
    comment: 'The customer service team goes above and beyond.',
    customerName: 'Michele M.',
    customerLocation: 'Pittsburgh, PA',
  },
  {
    comment: `Great customer service and great benefits too!`,
    customerName: 'Odis R.',
    customerLocation: 'Ralston, NE',
  },
]

const ProductPageSocialProofSection: FC = () => {
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current
      const { scrollLeft, scrollWidth, offsetWidth } = container

      // Get all testimonial cards
      const cards = container.querySelectorAll('.snap-center')

      if (cards.length === 0) return

      // Handle edge cases first
      // If scrolled all the way to the left, show first card
      if (scrollLeft <= 10) {
        setActiveIndex(0)
        return
      }

      // If scrolled near the end, show last card
      if (scrollLeft >= scrollWidth - offsetWidth - 10) {
        setActiveIndex(cards.length - 1)
        return
      }

      // Find which card is most visible/centered in the viewport
      let closestIndex = 0
      let closestDistance = Infinity

      cards.forEach((card, index) => {
        const cardElement = card as HTMLElement
        const cardLeft = cardElement.offsetLeft
        const cardWidth = cardElement.offsetWidth
        const cardCenter = cardLeft + cardWidth / 2
        const containerCenter = scrollLeft + offsetWidth / 2
        const distance = Math.abs(cardCenter - containerCenter)

        if (distance < closestDistance) {
          closestDistance = distance
          closestIndex = index
        }
      })

      setActiveIndex(closestIndex)
    }
  }

  const debouncedHandleScroll = () => {
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current)
    }

    // Update immediately for visual feedback
    handleScroll()

    // Also update after scroll settles (for snap-to behavior)
    scrollTimeoutRef.current = setTimeout(() => {
      handleScroll()
    }, 150)
  }

  const scrollToIndex = (index: number) => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current
      const cards = container.querySelectorAll('.snap-center')
      const targetCard = cards[index] as HTMLElement

      if (targetCard) {
        const cardLeft = targetCard.offsetLeft
        const containerWidth = container.offsetWidth
        const cardWidth = targetCard.offsetWidth
        const { scrollWidth } = container

        // Calculate ideal center position
        let scrollPosition = cardLeft - (containerWidth - cardWidth) / 2

        // Clamp to valid scroll range
        const maxScroll = scrollWidth - containerWidth
        scrollPosition = Math.max(0, Math.min(scrollPosition, maxScroll))

        container.scrollTo({
          left: scrollPosition,
          behavior: 'smooth',
        })

        // Update active index immediately for better UX
        setActiveIndex(index)
      }
    }
  }

  return (
    <div className="bg-green-900 py-16 lg:py-24 overflow-x-hidden" data-nosnippet>
      <GridContainer>
        <div className="col-span-full lg:col-span-6">
          <Typography
            variant="h2"
            sx={{ color: 'white' }}
            className="pb-6 lg:pb-12 text-center lg:text-left"
          >
            Trusted by over
            <br />3 million people
          </Typography>
          <div className="flex flex-col lg:flex-row gap-6 pb-6 lg:pb-0">
            {ratings.map((rating) => (
              <Rating key={rating.id} {...rating} />
            ))}
          </div>
        </div>
        <div className="col-span-full lg:col-span-6 flex flex-col gap-6">
          <div className="hidden lg:flex lg:flex-row gap-6 flex-1">
            {testimonials.map((testimonial, index) => (
              <div key={`testimonial-${index}`} className="flex-1 flex">
                <Testimonial {...testimonial} />
              </div>
            ))}
          </div>
          <div
            ref={scrollContainerRef}
            onScroll={debouncedHandleScroll}
            className={`
              flex flex-row gap-6 items-stretch
              overflow-x-auto snap-x snap-mandatory
              scrollbar-hide lg:hidden
              -mx-8 px-8 md:mx-0 md:px-0
            `}
          >
            {testimonials.map((testimonial, index) => (
              <div key={`testimonial-${index}`} className="flex-1 flex snap-center min-w-[182px]">
                <Testimonial {...testimonial} />
              </div>
            ))}
          </div>
          <div className="flex justify-center gap-2 sm:hidden">
            {testimonials.map((_, index) => (
              <button
                key={`dot-${index}`}
                onClick={() => scrollToIndex(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  activeIndex === index ? 'bg-green-600' : 'bg-ink-400'
                }`}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </GridContainer>
    </div>
  )
}

export default ProductPageSocialProofSection
